import type { TimeEntry } from '@catalyst-psa/shared';

/** Which billing state a time entry is in, for its badge + the status filter. */
export type BillingStatusKey =
  | 'invoiced'
  | 'external'
  | 'writtenoff'
  | 'nonbillable'
  | 'unbilled';

export interface BillingStatus {
  key: BillingStatusKey;
  label: string;
  tone: 'green' | 'gray' | 'amber' | 'red' | 'blue';
  /** Extra context shown after the label (invoice number / external ref), or null. */
  detail: string | null;
}

/**
 * Derives a time entry's billing status (first match wins): written off → invoiced (PSA
 * invoice) → billed externally → non-billable → unbilled (billable, not yet invoiced).
 */
export function billingStatusOf(entry: TimeEntry): BillingStatus {
  if (entry.writtenOff) return { key: 'writtenoff', label: 'Written off', tone: 'red', detail: null };
  if (entry.invoiceId)
    return {
      key: 'invoiced',
      label: 'Invoiced',
      tone: 'green',
      detail: entry.invoiceNumber ?? null,
    };
  if (entry.billedExternally)
    return {
      key: 'external',
      label: 'Billed externally',
      tone: 'blue',
      detail: entry.externalInvoiceRef ?? null,
    };
  if (!entry.billable)
    return { key: 'nonbillable', label: 'Non-billable', tone: 'gray', detail: null };
  return { key: 'unbilled', label: 'Unbilled', tone: 'amber', detail: null };
}

/** Options for a billing-status filter dropdown. */
export const BILLING_STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'billable', label: 'Billable' },
  { value: 'nonbillable', label: 'Non-billable' },
  { value: 'unbilled', label: 'Unbilled' },
  { value: 'invoiced', label: 'Invoiced' },
  { value: 'external', label: 'Billed externally' },
  { value: 'writtenoff', label: 'Written off' },
] as const;

/** True when an entry passes the given billing-status filter value. */
export function matchesBillingStatusFilter(entry: TimeEntry, filter: string): boolean {
  if (filter === 'all') return true;
  if (filter === 'billable') return entry.billable;
  if (filter === 'nonbillable') return !entry.billable;
  return billingStatusOf(entry).key === filter;
}
